(function initializeProductMediaGallery(root) {
  'use strict';

  const mediaFilter = root.PmgMediaFilter;
  if (!mediaFilter) {
    console.error('[PMG] pmg-media-filter.js must load before product-media-gallery.js.');
    return;
  }

  class ProductMediaGallery extends HTMLElement {
    #controller = null;
    #thumbsResizeObserver = null;
    #bodyOverflowBeforeZoom = '';

    connectedCallback() {
      this.#controller?.abort();
      this.#thumbsResizeObserver?.disconnect();
      this.#controller = new AbortController();
      const signal = this.#controller.signal;

      this.slides = Array.from(this.querySelectorAll('.pmg__slide'));
      this.thumbs = Array.from(this.querySelectorAll('.pmg__thumb'));
      this.dots = Array.from(this.querySelectorAll('.pmg__dot'));
      this.zoomBtn = this.querySelector('[data-pmg-zoom]');
      this.lightbox = this.querySelector('[data-pmg-lightbox]');
      this.lightboxImg = this.querySelector('[data-pmg-lightbox-img]');
      this.thumbsList = this.querySelector('[data-pmg-thumbs-list]');
      this.debugPanel = this.querySelector('[data-pmg-debug]');
      this.debugAlt = this.querySelector('[data-pmg-debug-alt]');
      this.debugSelection = this.querySelector('[data-pmg-debug-selection]');
      this.debugResult = this.querySelector('[data-pmg-debug-result]');
      this.productId = String(this.dataset.pmgProductId || '');
      this.current = 0;
      this.visibleIndexes = [];
      this.lightboxOpen = false;
      this.debugEnabled = false;
      /*
      PMG 调试开关（当前已关闭，需要时恢复下面的赋值）
      this.debugEnabled =
        this.dataset.pmgDebugEnabled === 'true' ||
        Boolean(root.Shopify?.designMode) ||
        root.location.hostname === '127.0.0.1' ||
        root.location.hostname === 'localhost' ||
        new URLSearchParams(root.location.search).get('pmg_debug') === '1';
      */
      if (this.debugPanel) this.debugPanel.hidden = !this.debugEnabled;

      this.mediaEntries = mediaFilter.preparePmgMedia(
        this.slides.map((slide, index) => ({
          index,
          mediaId: slide.dataset.pmgMediaId,
          rawTags: slide.dataset.pmgTags,
        }))
      );

      this.#warnAboutInvalidTags();
      this.#sanitizeAccessibleText();

      this.thumbs.concat(this.dots).forEach((control) => {
        control.addEventListener(
          'click',
          () => {
            this.setActive(this.#getControlIndex(control));
          },
          { signal }
        );
      });

      this.#bindThumbsScroll();
      this.#bindHoverZoom();
      this.#bindMediaMetadata();
      this.#bindStageGestures();

      if (this.zoomBtn) {
        this.zoomBtn.addEventListener(
          'click',
          (event) => {
            event.stopPropagation();
            this.enterHoverZoom();
          },
          { signal }
        );
      }

      if (this.lightbox) {
        this.lightbox.addEventListener(
          'click',
          (event) => {
            if (
              event.target === this.lightbox ||
              event.target.closest('[data-pmg-lightbox-close]')
            ) {
              this.closeZoom();
            }
          },
          { signal }
        );
        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape' && this.lightboxOpen) this.closeZoom();
          },
          { signal }
        );
      }

      document.addEventListener(
        'pi:variant-changed',
        (event) => this.#handleVariantChange(event),
        { signal }
      );

      this.filterBySelection({
        options: this.#readInitialOptions(),
        featuredMediaId: this.dataset.pmgInitialMediaId || null,
      });
    }

    disconnectedCallback() {
      this.#controller?.abort();
      this.#controller = null;
      this.#thumbsResizeObserver?.disconnect();
      this.#thumbsResizeObserver = null;
      this.closeZoom();
      this.exitHoverZoom();
    }

    #readInitialOptions() {
      try {
        const options = JSON.parse(this.dataset.pmgInitialOptions || '[]');
        return Array.isArray(options) ? options : [];
      } catch (error) {
        if (root.Shopify?.designMode) {
          console.warn('[PMG] Invalid initial variant options.', error);
        }
        return [];
      }
    }

    #handleVariantChange(event) {
      const detail = event.detail || {};
      const eventProductId = String(detail.productId == null ? '' : detail.productId);
      if (eventProductId && this.productId && eventProductId !== this.productId) return;

      const variant = detail.variant || null;
      this.filterBySelection({
        options: variant && Array.isArray(variant.options) ? variant.options : [],
        featuredMediaId: variant ? variant.media : null,
      });
    }

    #warnAboutInvalidTags() {
      if (!root.Shopify?.designMode) return;

      this.mediaEntries.forEach((entry) => {
        if (!entry.issues.length) return;
        console.warn('[PMG] Media tag validation issue.', {
          productId: this.productId,
          mediaId: entry.mediaId,
          issues: entry.issues,
        });
      });
    }

    #sanitizeAccessibleText() {
      this.slides.forEach((slide, index) => {
        const cleanAlt =
          mediaFilter.stripPmgTags(slide.dataset.pmgTags || '') ||
          this.dataset.pmgFallbackAlt ||
          '';
        if (!cleanAlt) return;

        slide.querySelectorAll('img[alt]').forEach((image) => {
          image.alt = cleanAlt;
        });

        const thumb = this.thumbs.find(
          (control) => this.#getControlIndex(control) === index
        );
        if (thumb) {
          thumb.setAttribute('aria-label', cleanAlt);
          const thumbImage = thumb.querySelector('img[alt]');
          if (thumbImage) thumbImage.alt = cleanAlt;
        }
      });
    }

    #bindMediaMetadata() {
      this.querySelectorAll('.pmg__media-frame video').forEach((video) => {
        const updateAspectRatio = () => {
          const width = Number(video.videoWidth);
          const height = Number(video.videoHeight);
          if (!(width > 0) || !(height > 0)) return;

          const frame = video.closest('[data-pmg-media-frame]');
          const slide = video.closest('.pmg__slide');
          if (!frame || !slide) return;

          this.#applyMediaAspectRatio(frame, slide, width / height);
        };

        if (video.readyState >= 1) updateAspectRatio();
        video.addEventListener('loadedmetadata', updateAspectRatio, {
          signal: this.#controller.signal,
        });
      });
    }

    #applyMediaAspectRatio(frame, slide, ratio) {
      if (!Number.isFinite(ratio) || ratio <= 0) return;

      const orientation = ratio < 0.99 ? 'portrait' : ratio > 1.01 ? 'landscape' : 'square';
      const orientationClasses = ['portrait', 'landscape', 'square'];

      orientationClasses.forEach((value) => {
        frame.classList.toggle(`pmg__media-frame--${value}`, value === orientation);
        slide.classList.toggle(`pmg__slide--${value}`, value === orientation);
      });
      frame.style.setProperty('--pmg-media-aspect-ratio', String(ratio));
      slide.style.setProperty('--pmg-media-aspect-ratio', String(ratio));
      slide.dataset.mediaOrientation = orientation;

      if (this.slides[this.current] === slide) this.#updateStageMediaState(slide);
    }

    #updateStageMediaState(slide) {
      if (!this.stage || !slide) return;
      this.stage.dataset.activeMediaType = slide.dataset.mediaType || '';
      this.stage.dataset.activeMediaOrientation = slide.dataset.mediaOrientation || 'square';
    }

    #getControlIndex(control) {
      const index = Number.parseInt(control?.dataset.pmgIndex || '', 10);
      return Number.isInteger(index) ? index : -1;
    }

    filterByVariant(variant) {
      this.filterBySelection({
        options: variant && Array.isArray(variant.options) ? variant.options : [],
        featuredMediaId: variant ? variant.media : null,
      });
    }

    filterBySelection(selection) {
      this.currentSelection = {
        options: Array.isArray(selection?.options) ? selection.options.slice() : [],
        featuredMediaId: selection?.featuredMediaId ?? null,
      };
      const result = mediaFilter.resolvePmgMedia(
        this.mediaEntries,
        this.currentSelection
      );
      this.filterResult = result;
      this.dataset.pmgState = result.reason;
      this.showIndexes(result.indexes);
    }

    showIndexes(indexes) {
      const validIndexes = [];
      const seen = new Set();

      (Array.isArray(indexes) ? indexes : []).forEach((index) => {
        const numericIndex = Number(index);
        if (
          !Number.isInteger(numericIndex) ||
          numericIndex < 0 ||
          numericIndex >= this.slides.length ||
          seen.has(numericIndex)
        ) {
          return;
        }
        seen.add(numericIndex);
        validIndexes.push(numericIndex);
      });

      if (!validIndexes.length && this.slides.length) validIndexes.push(0);

      const visibleSet = new Set(validIndexes);
      this.visibleIndexes = validIndexes;
      this.dataset.count = String(validIndexes.length);

      this.slides.forEach((slide, index) => {
        slide.hidden = !visibleSet.has(index);
      });
      this.thumbs.forEach((thumb) => {
        const index = this.#getControlIndex(thumb);
        const isVisible = visibleSet.has(index);
        const item = thumb.closest('li');
        if (item) item.hidden = !isVisible;
        thumb.hidden = !isVisible;
      });
      this.dots.forEach((dot) => {
        dot.hidden = !visibleSet.has(this.#getControlIndex(dot));
      });

      if (this.dots.length) {
        const dotsWrap = this.dots[0].parentElement;
        if (dotsWrap) dotsWrap.hidden = validIndexes.length <= 1;
      }

      this.applyVisibleOrder(validIndexes);
      this.setActive(validIndexes[0] ?? -1);
      requestAnimationFrame(() => this.#updateThumbNav());
    }

    applyVisibleOrder(indexes) {
      const orderMap = new Map(indexes.map((index, order) => [index, order]));
      const hiddenOrder = indexes.length;

      this.slides.forEach((slide, index) => {
        slide.style.order = String(orderMap.get(index) ?? hiddenOrder + index);
      });
      this.thumbs.forEach((thumb) => {
        const index = this.#getControlIndex(thumb);
        const item = thumb.closest('li') || thumb;
        item.style.order = String(orderMap.get(index) ?? hiddenOrder + Math.max(index, 0));
      });
      this.dots.forEach((dot) => {
        const index = this.#getControlIndex(dot);
        dot.style.order = String(orderMap.get(index) ?? hiddenOrder + Math.max(index, 0));
      });
    }

    #updateDebugPanel() {
      if (!this.debugEnabled || !this.debugPanel) return;

      const slide = this.slides[this.current];
      const entry = this.mediaEntries[this.current];
      if (!slide || !entry) return;

      const inspection = mediaFilter.inspectPmgEntry(
        entry,
        this.currentSelection
      );
      const reason = this.filterResult?.reason || 'unknown';
      const selectedOptions = this.currentSelection?.options || [];
      let executionText = `状态：${reason}`;

      if (reason === 'all') {
        executionText = '执行成功：商品没有 PMG 标签，显示全部图片';
      } else if (reason === 'matched' && inspection.matchedKeys.length) {
        executionText = `匹配成功：${inspection.matchedKeys
          .map((key) => `[pmg:${key}]`)
          .join('、')}`;
      } else if (reason === 'default' && inspection.default) {
        executionText = '回退成功：[pmg:default]';
      } else if (
        reason === 'featured' &&
        entry.mediaId === String(this.currentSelection?.featuredMediaId ?? '')
      ) {
        executionText = `回退成功：当前变体关联媒体 ${entry.mediaId}`;
      } else if (inspection.common) {
        executionText = entry.hasDirective
          ? '执行成功：显式公共图片 [pmg:common]'
          : '执行成功：无 PMG 标签，按公共图片展示';
      } else if (reason === 'fallback') {
        executionText = '安全兜底：没有匹配、default、公共图或关联媒体';
      }

      if (inspection.issues.length) {
        executionText += `；标签警告：${inspection.issues
          .map((issue) => issue.code)
          .join(', ')}`;
      }

      if (this.debugAlt) {
        this.debugAlt.textContent = slide.dataset.pmgTags || '(空 ALT)';
      }
      if (this.debugSelection) {
        this.debugSelection.textContent = selectedOptions.length
          ? selectedOptions.join(' | ')
          : '(无有效变体)';
      }
      if (this.debugResult) {
        this.debugResult.textContent = executionText;
      }
    }

    #bindThumbsScroll() {
      const list = this.thumbsList;
      if (!list) return;

      this.navButtons = Array.from(this.querySelectorAll('[data-pmg-thumbs]'));
      this.navButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const amount = list.clientHeight * 0.8;
            const direction = button.dataset.pmgThumbs === 'up' ? -1 : 1;
            list.scrollBy({ top: direction * amount, behavior: 'smooth' });
          },
          { signal: this.#controller.signal }
        );
      });

      this.#updateThumbNav();
      if ('ResizeObserver' in root) {
        this.#thumbsResizeObserver = new ResizeObserver(() => this.#updateThumbNav());
        this.#thumbsResizeObserver.observe(list);
      }
      root.addEventListener('resize', () => this.#updateThumbNav(), {
        signal: this.#controller.signal,
      });
      list.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener('load', () => this.#updateThumbNav(), {
            once: true,
            signal: this.#controller.signal,
          });
        }
      });

      let isDown = false;
      let startY = 0;
      let startScroll = 0;
      let moved = false;

      list.addEventListener(
        'pointerdown',
        (event) => {
          isDown = true;
          moved = false;
          startY = event.clientY;
          startScroll = list.scrollTop;
          list.classList.add('is-dragging');
        },
        { signal: this.#controller.signal }
      );

      list.addEventListener(
        'pointermove',
        (event) => {
          if (!isDown) return;
          const delta = event.clientY - startY;
          if (Math.abs(delta) > 3) moved = true;
          list.scrollTop = startScroll - delta;
        },
        { signal: this.#controller.signal }
      );

      const endDrag = (event) => {
        if (!isDown) return;
        isDown = false;
        list.classList.remove('is-dragging');
        if (!moved || !event?.target) return;

        const thumb = event.target.closest('.pmg__thumb');
        if (!thumb) return;
        const blockClick = (clickEvent) => {
          clickEvent.stopPropagation();
          clickEvent.preventDefault();
          thumb.removeEventListener('click', blockClick, true);
        };
        thumb.addEventListener('click', blockClick, {
          capture: true,
          signal: this.#controller.signal,
        });
      };

      list.addEventListener('pointerup', endDrag, { signal: this.#controller.signal });
      list.addEventListener('pointerleave', endDrag, { signal: this.#controller.signal });
      list.addEventListener('pointercancel', endDrag, { signal: this.#controller.signal });
    }

    #updateThumbNav() {
      const list = this.thumbsList;
      if (!list || !this.navButtons) return;
      const scrollable = list.scrollHeight - list.clientHeight > 1;
      this.navButtons.forEach((button) => {
        button.hidden = !scrollable;
      });
    }

    #bindHoverZoom() {
      const stage = this.querySelector('.pmg__stage');
      if (!stage) return;

      this.stage = stage;
      this.zoomMq = root.matchMedia('(min-width: 1000px) and (hover: hover)');
      this.zoomScale = 2.5;
      this.zoomImg = null;
      this.zoomActive = false;

      stage.addEventListener(
        'pointermove',
        (event) => {
          if (!this.zoomActive || !this.zoomImg) return;
          const rect = this.zoomImg.getBoundingClientRect();
          let x = ((event.clientX - rect.left) / rect.width) * 100;
          let y = ((event.clientY - rect.top) / rect.height) * 100;
          x = Math.max(0, Math.min(100, x));
          y = Math.max(0, Math.min(100, y));
          this.zoomImg.style.transformOrigin = `${x}% ${y}%`;
        },
        { signal: this.#controller.signal }
      );

      stage.addEventListener(
        'click',
        () => {
          if (this.zoomActive) this.exitHoverZoom();
        },
        { signal: this.#controller.signal }
      );

      this.zoomMq.addEventListener(
        'change',
        () => {
          if (!this.zoomMq.matches && this.zoomActive) this.exitHoverZoom();
          this.#updateZoomBtn();
        },
        { signal: this.#controller.signal }
      );
    }

    #bindStageGestures() {
      const stage = this.stage || this.querySelector('.pmg__stage');
      if (!stage) return;

      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let gestureActive = false;

      stage.addEventListener(
        'pointerdown',
        (event) => {
          if (this.zoomMq?.matches) return;
          gestureActive = false;
          const slide = this.slides[this.current];
          if (!slide || slide.dataset.mediaType !== 'image') return;
          if (event.target.closest?.('button, a, input, select, textarea, [role="button"]')) return;

          gestureActive = true;
          startX = event.clientX;
          startY = event.clientY;
          startTime = Date.now();
        },
        { signal: this.#controller.signal }
      );

      stage.addEventListener(
        'pointerup',
        (event) => {
          if (this.zoomMq?.matches || !gestureActive) return;
          gestureActive = false;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          const absoluteX = Math.abs(dx);
          const absoluteY = Math.abs(dy);

          if (absoluteX > 40 && absoluteX > absoluteY) {
            if (dx < 0) this.next();
            else this.prev();
          } else if (
            absoluteX < 10 &&
            absoluteY < 10 &&
            Date.now() - startTime < 500
          ) {
            this.openZoom();
          }
        },
        { signal: this.#controller.signal }
      );

      const cancelGesture = () => {
        gestureActive = false;
      };
      stage.addEventListener('pointercancel', cancelGesture, {
        signal: this.#controller.signal,
      });
      stage.addEventListener('pointerleave', cancelGesture, {
        signal: this.#controller.signal,
      });
    }

    next() {
      if (!this.visibleIndexes.length) return;
      const currentVisibleIndex = this.visibleIndexes.indexOf(this.current);
      const nextVisibleIndex =
        currentVisibleIndex >= 0
          ? (currentVisibleIndex + 1) % this.visibleIndexes.length
          : 0;
      this.setActive(this.visibleIndexes[nextVisibleIndex]);
    }

    prev() {
      if (!this.visibleIndexes.length) return;
      const currentVisibleIndex = this.visibleIndexes.indexOf(this.current);
      const prevVisibleIndex =
        currentVisibleIndex >= 0
          ? (currentVisibleIndex - 1 + this.visibleIndexes.length) %
            this.visibleIndexes.length
          : 0;
      this.setActive(this.visibleIndexes[prevVisibleIndex]);
    }

    enterHoverZoom() {
      const slide = this.slides[this.current];
      if (!slide || slide.dataset.mediaType !== 'image') return;
      const image = slide.querySelector('img');
      if (!image) return;

      this.zoomImg = image;
      this.zoomActive = true;
      this.stage?.classList.add('is-zooming');
      image.style.transition = 'transform 0.12s ease-out';
      image.style.transform = `scale(${this.zoomScale})`;
      this.#updateZoomBtn();
    }

    exitHoverZoom() {
      this.zoomActive = false;
      this.stage?.classList.remove('is-zooming');
      if (this.zoomImg) {
        this.zoomImg.style.transform = '';
        this.zoomImg.style.transformOrigin = '';
        this.zoomImg.style.transition = '';
        this.zoomImg = null;
      }
      this.#updateZoomBtn();
    }

    #updateZoomBtn() {
      if (!this.zoomBtn) return;
      const slide = this.slides[this.current];
      const isImage = Boolean(slide && slide.dataset.mediaType === 'image');
      const isDesktop = Boolean(this.zoomMq?.matches);
      this.zoomBtn.hidden = !(isImage && isDesktop) || this.zoomActive;
    }

    setActive(index) {
      let nextIndex = Number(index);
      if (!Number.isInteger(nextIndex)) return;
      if (!this.visibleIndexes.includes(nextIndex)) {
        nextIndex = this.visibleIndexes[0];
      }
      if (!Number.isInteger(nextIndex)) return;

      this.current = nextIndex;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === nextIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (!active) {
          const video = slide.querySelector('video');
          if (video) video.pause();
        }
      });
      this.#updateStageMediaState(this.slides[nextIndex]);
      this.thumbs.forEach((thumb) => {
        const active = this.#getControlIndex(thumb) === nextIndex;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot) => {
        const active = this.#getControlIndex(dot) === nextIndex;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      if (this.zoomActive) this.exitHoverZoom();
      this.#updateZoomBtn();
      this.#updateDebugPanel();
    }

    openZoom() {
      const activeSlide = this.slides[this.current];
      if (!activeSlide) return;
      const image = activeSlide.querySelector('[data-pmg-zoom-src]');
      if (!image || !this.lightbox || !this.lightboxImg) return;

      this.lightboxImg.src = image.dataset.pmgZoomSrc;
      this.lightboxImg.alt = image.alt || this.lightboxImg.alt;
      this.lightbox.hidden = false;
      this.lightboxOpen = true;
      this.#bodyOverflowBeforeZoom = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    closeZoom() {
      if (!this.lightboxOpen) return;
      this.lightboxOpen = false;
      if (this.lightbox) this.lightbox.hidden = true;
      if (this.lightboxImg) this.lightboxImg.removeAttribute('src');
      document.body.style.overflow = this.#bodyOverflowBeforeZoom;
      this.#bodyOverflowBeforeZoom = '';
    }
  }

  if (!customElements.get('product-media-gallery')) {
    customElements.define('product-media-gallery', ProductMediaGallery);
  }
})(window);
